import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Paper,
  Title,
  Table,
  Group,
  Button,
  Text,
  Stack,
  SimpleGrid,
  Badge,
  Anchor,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { CodeHighlight } from "@mantine/code-highlight";
import { PageLoader } from "@/components/common";
import { adminApi, filtersApi } from "@/api/client";

const reasonLabels: Record<string, string> = {
  first_layer: "First Layer",
  amenities: "Amenities",
  electricity: "Electricity",
  heating: "Heating",
  internet: "Internet",
  metro_access: "Metro Access",
  pets: "Pets",
};

const reasonColors: Record<string, string> = {
  first_layer: "gray",
  amenities: "orange",
  electricity: "yellow",
  heating: "red",
  internet: "blue",
  metro_access: "grape",
  pets: "pink",
};

export const Route = createFileRoute("/_authenticated/admin/rejections")({
  validateSearch: (search: Record<string, unknown>) => ({
    reason: search.reason as string | undefined,
    filter_id: search.filter_id ? Number(search.filter_id) : undefined,
  }),
  component: AdminRejectionsPage,
});

function AdminRejectionsPage() {
  const { reason, filter_id } = Route.useSearch();

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ["admin", "rejections", "stats"],
    queryFn: adminApi.getRejectionStats,
  });

  const { data: rejections, isLoading: rejectionsLoading } = useQuery({
    queryKey: ["admin", "rejections", { reason, filter_id }],
    queryFn: () => adminApi.getRejections({ reason, filter_id, limit: 100 }),
  });

  const { data: filters } = useQuery({
    queryKey: ["filters"],
    queryFn: filtersApi.list,
  });

  if (statsLoading || rejectionsLoading) {
    return <PageLoader />;
  }

  const reasonFilters = [
    { value: undefined, label: "All" },
    ...Object.entries(stats?.by_reason || {}).map(([key, count]) => ({
      value: key,
      label: `${reasonLabels[key] || key} (${count})`,
    })),
  ];

  // biome-ignore lint/suspicious/noExplicitAny: API rejection details have multiple legacy shapes.
  const getFilterNames = (rejection: any) => {
    // New format: filters array in rejection_details
    const filtersArr = rejection.rejection_details?.filters as
      | { filter_id: number; filter_name: string; reason: string }[]
      | undefined;
    if (filtersArr && filtersArr.length > 0) {
      return filtersArr.map((f: { filter_name: string }) => f.filter_name).join(", ");
    }
    // Legacy format: single filter_id
    if (rejection.filter_id) {
      const filter = filters?.find((f: { id: number }) => f.id === rejection.filter_id);
      return filter?.name || `Filter #${rejection.filter_id}`;
    }
    return "-";
  };

  const formatDetails = (details: Record<string, unknown> | undefined) => {
    if (!details) return "-";
    return JSON.stringify(details, null, 2);
  };

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={1}>Rejected Apartments</Title>
        <Text c="dimmed">Track apartments rejected by second-layer filtering</Text>
      </Group>

      <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
        <Paper
          p="md"
          radius="md"
          withBorder
          style={{ borderColor: "#0f3460", textAlign: "center" }}
        >
          <Text size="xl" fw={700} c="primary">
            {stats?.total || 0}
          </Text>
          <Text size="sm" c="dimmed">
            Total Rejections
          </Text>
        </Paper>
        {Object.entries(stats?.by_reason || {})
          .slice(0, 3)
          .map(([key, count]) => (
            <Paper
              key={key}
              p="md"
              radius="md"
              withBorder
              style={{ borderColor: "#0f3460", textAlign: "center" }}
            >
              <Text size="xl" fw={700} c={reasonColors[key] || "dimmed"}>
                {count}
              </Text>
              <Text size="sm" c="dimmed">
                {reasonLabels[key] || key}
              </Text>
            </Paper>
          ))}
      </SimpleGrid>

      <Group gap="xs">
        {reasonFilters.map((filter) => (
          <Button
            key={filter.value || "all"}
            component={Link}
            to="/admin/rejections"
            search={{ reason: filter.value, filter_id }}
            variant="filled"
            color={reason === filter.value ? "primary" : "#0f3460"}
            radius="xl"
            size="sm"
          >
            {filter.label}
          </Button>
        ))}
      </Group>

      <Paper p="md" radius="md" withBorder style={{ borderColor: "#0f3460" }}>
        <Table.ScrollContainer minWidth={900}>
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Qasa ID</Table.Th>
                <Table.Th>Title</Table.Th>
                <Table.Th>Rent</Table.Th>
                <Table.Th>Rooms</Table.Th>
                <Table.Th>Filter</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Details</Table.Th>
                <Table.Th>Rejected At</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rejections?.map((rejection) => (
                <Table.Tr key={rejection.id}>
                  <Table.Td>
                    <Anchor
                      href={`https://qasa.com/se/en/home/${rejection.qasa_id}`}
                      target="_blank"
                      c="primary"
                    >
                      {rejection.qasa_id}
                    </Anchor>
                  </Table.Td>
                  <Table.Td>
                    <Text lineClamp={1} maw={200}>
                      {rejection.title || "-"}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {rejection.rent ? `${rejection.rent.toLocaleString()} kr` : "-"}
                  </Table.Td>
                  <Table.Td>{rejection.rooms ?? "-"}</Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {getFilterNames(rejection)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      color={reasonColors[rejection.rejection_reason] || "gray"}
                      variant="light"
                    >
                      {reasonLabels[rejection.rejection_reason] || rejection.rejection_reason}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    {rejection.rejection_details ? (
                      <Text
                        size="xs"
                        c="primary"
                        style={{ cursor: "pointer" }}
                        onClick={() =>
                          modals.open({
                            title: "Rejection Details",
                            size: "lg",
                            children: (
                              <CodeHighlight
                                code={formatDetails(rejection.rejection_details)}
                                language="json"
                              />
                            ),
                          })
                        }
                      >
                        View details
                      </Text>
                    ) : (
                      <Text size="xs" c="dimmed">
                        -
                      </Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm" c="dimmed">
                      {new Date(rejection.rejected_at).toLocaleString()}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ))}
              {(!rejections || rejections.length === 0) && (
                <Table.Tr>
                  <Table.Td colSpan={8}>
                    <Text ta="center" c="dimmed" py="lg">
                      No rejected apartments found
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </Paper>
    </Stack>
  );
}
